import { Component, computed, inject, input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CategoryVTO } from '../../../../pages/admin-dashboard/categories/category.model';
import { ProductsLookupService } from '../../../../services/product/lookup.service';
import { LanguageService } from '../../../../services/language.service';
import { ProductService } from '../../../../services/product/product.service';
import {
  ProductVTO,
  ProductFiltersApi,
  ProductResultSet,
} from '../../../../pages/admin-dashboard/products/product.model';
import { CategoryService } from '../../../../services/category/category.service';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { LookupResultSet, LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';

@Component({
  selector: 'app-view-category',
  imports: [FormsModule, CommonModule],
  templateUrl: './view-category.html',
  styleUrls: ['./view-category.scss'],
})
export class ViewCategory implements OnInit {
  id = input<string>();
  private lookupService = inject(ProductsLookupService);
  private productService = inject(ProductService);
  private platformId = inject(PLATFORM_ID);
  private languageService = inject(LanguageService);
  private categoryService = inject(CategoryService);
  private fileMgt = inject(ProductsFileMgt);
  activeCategoryTitle = signal<string>('');

  category = signal<CategoryVTO | undefined>(undefined);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  categories = signal<LookupResultSet | undefined>(undefined);
  subCategories = signal<LookupResultSet | undefined>(undefined);
  manufacturers = signal<LookupResultSet | undefined>(undefined);
  productImages = new Map<string, { url: SafeUrl; isImage: boolean }>();
  products = signal<ProductVTO[]>([]);
  currentPage = signal<number>(1);
  pageSize = signal<number>(8);

  filters: ProductFiltersApi = {
    hasSubProducts: '',
    productTitle: '',
    categoryId: undefined,
    subCategoryId: undefined,
    mfgCompanyId: undefined,
    customerPriceFrom: 0,
    customerPriceTo: 0,
    businessPriceFrom: 0,
    businessPriceTo: 0,
    orderBy: 'CREATION_DATE',
    orderDir: 'ASC',
  };

  paginatedProducts = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize();
    const endIndex = startIndex + this.pageSize();
    return this.products().slice(startIndex, endIndex);
  });

  onCategoryChange(newCategoryId: number | undefined) {
    this.filters.subCategoryId = undefined;
    this.subCategories.set(undefined);

    if (newCategoryId) {
      this.categoryService.getSubcategories({ categoryId: newCategoryId }).subscribe({
        next: (res) => this.subCategories.set(res),
        error: () => console.error('Error fetching subcategories'),
      });
    }
  }
  totalPages = computed(() => Math.ceil(this.products().length / this.pageSize()));

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const storedCategory = sessionStorage.getItem('selectedCategory');
      if (storedCategory) {
        const cat = JSON.parse(storedCategory);
        this.category.set(cat);
        this.filters.categoryId = cat.id;
        this.activeCategoryTitle.set(this.getLocalizedTitle(cat));

        this.loadFilterOptions();
        this.onCategoryChange(cat.id);
        this.loadProducts();
      } else {
        this.error.set(
          $localize`:@@viewCategoryNoDataError:No category data found. Please navigate from the categories list.`,
        );
        this.loading.set(false);
      }
    }
  }

  loadFilterOptions() {
    this.lookupService.getLookupCategories().subscribe((res) => this.categories.set(res));
    this.lookupService.getLookupMFGCompanies().subscribe((res) => this.manufacturers.set(res));
  }

  loadProducts() {
    this.loading.set(true);

    const locale = this.languageService.getCurrentLocale();
    const filtersToUse: ProductFiltersApi = { ...this.filters };

    // if (filtersToUse.productTitle) {
    //   if (locale === 'ar') {
    //     filtersToUse.productTitleAr = filtersToUse.productTitle;
    //     delete filtersToUse.productTitle;
    //   } else {
    //     filtersToUse.productTitleEn = filtersToUse.productTitle;
    //     delete filtersToUse.productTitle;
    //   }
    // }

    this.productService.getAllProductsByFilter(filtersToUse).subscribe({
      next: (res: ProductResultSet) => {
        const list = res.list || [];
        this.products.set(list);

        list.forEach((product) => {
          if (product.profileImageFid) {
            this.fileMgt.getImageUrl(product.profileImageFid).subscribe({
              next: (data) => this.productImages.set(product.id.toString(), data),
              error: () => this.productImages.set(product.id.toString(), { url: '', isImage: false }),
            });
          }
        });

        this.loading.set(false);
      },
      error: () => {
        this.error.set('Failed to load products');
        this.loading.set(false);
      },
    });
  }

  getSliderTrackStyle(from: number, to: number) {
    const min = 0;
    const max = 1000;
    const start = ((from - min) / (max - min)) * 100;
    const end = ((to - min) / (max - min)) * 100;
    return {
      background: `linear-gradient(to right, #e5e7eb ${start}%, #C42727 ${start}%, #C42727 ${end}%, #e5e7eb ${end}%)`,
    };
  }
  validateRange(type: 'people' | 'pharmacy') {
    const filters = this.filters;

    if (type === 'people') {
      if ((filters.customerPriceFrom ?? 0) > (filters.customerPriceTo ?? 0)) {
        filters.customerPriceFrom = filters.customerPriceTo;
      }
    } else {
      if ((filters.businessPriceFrom ?? 0) > (filters.businessPriceTo ?? 0)) {
        filters.businessPriceFrom = filters.businessPriceTo;
      }
    }
  }

  applyFilters() {
    const selectedCatId = this.filters.categoryId;
    const foundCat = this.categories()?.list.find((c: any) => c.id === selectedCatId);

    if (foundCat) {
      this.activeCategoryTitle.set(this.getLocalizedTitle(foundCat));
    }
    this.currentPage.set(1);
    this.loadProducts();
  }

  resetFilters() {
    this.filters = {
      hasSubProducts: '',
      productTitle: '',
      categoryId: this.category()?.id,
      //subCategoryId: undefined,
      mfgCompanyId: undefined,
      customerPriceFrom: 0,
      customerPriceTo: 0,
      businessPriceFrom: 0,
      businessPriceTo: 0,
      orderBy: 'CREATION_DATE',
      orderDir: 'ASC',
    };
    this.applyFilters();
  }

  toggleSortOrder() {
    this.filters.orderDir = this.filters.orderDir === 'ASC' ? 'DESC' : 'ASC';
    this.applyFilters();
  }

  getLocalizedTitle(item: CategoryVTO | LookupVTO | undefined): string {
    if (!item) return '';
    return this.languageService.getCurrentLocale() === 'ar' ? item.titleAr || item.titleEn : item.titleEn;
  }

  getLocalizedSortOrder(): string {
    const isAr = this.languageService.getCurrentLocale() === 'ar';
    return this.filters.orderDir === 'ASC' ? (isAr ? 'تصاعدي' : 'ASC') : isAr ? 'تنازلي' : 'DESC';
  }

  getDiscountedPrice(price?: number, discount?: number): number {
    if (!price) return 0;
    return discount ? price - (price * discount) / 100 : price;
  }

  onAddToCart(product: ProductVTO) {
    console.log('Added to cart:', product);
  }

  onAddToWishlist(product: ProductVTO) {
    console.log('Added to wishlist:', product);
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages()) this.currentPage.set(page);
  }

  get pagesArray() {
    return Array(this.totalPages())
      .fill(0)
      .map((_, i) => i + 1);
  }
}
